import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg } from "../../lib/api";

export const OUTCOMES = { addressed: "Need addressed", partially: "Partially addressed", not_yet: "Not yet addressed", unclear: "Evidence unclear" };
const FIELDS = [["evaluation_findings", "Evaluation findings: did participants receive meaningful support? *"], ["outcome_evidence", "Evidence of outcome (Level 3)"],
  ["challenges", "Challenges encountered"], ["lessons_learned", "Lessons learned"], ["recommended_improvements", "Recommended improvements"]];

const Box = ({ title, children, testId }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid={testId}>
    <h2 className="mb-3 font-serif-display text-xl font-semibold text-amber-900">{title}</h2>{children}
  </section>
);

export const EvaluationCard = ({ plan, onSaved }) => {
  const [f, setF] = useState(() => Object.fromEntries([...FIELDS.map(([k]) => [k, plan[k] || ""]), ["outcome_rating", plan.outcome_rating || ""]]));
  const save = async () => {
    try { await api.patch(`/plans/${plan.plan_id}`, { ...f, outcome_rating: f.outcome_rating || null }); toast.success("Evaluation saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!plan.can.evaluate) {
    return (
      <Box title="Evaluation & reflection" testId="evaluation-card">
        {plan.outcome_rating && <p className="mb-2 text-sm font-semibold text-[#285E3B]">{OUTCOMES[plan.outcome_rating]}</p>}
        {FIELDS.map(([k, l]) => plan[k] ? <div key={k} className="mb-2"><p className="text-xs text-stone-500">{l.replace(" *", "")}</p><p className="text-sm text-stone-800 whitespace-pre-wrap">{plan[k]}</p></div> : null)}
        {!plan.evaluation_findings && <p className="text-sm text-stone-500">Recorded after completion.</p>}
      </Box>
    );
  }
  return (
    <Box title="Evaluation & reflection" testId="evaluation-card">
      <div className="space-y-3">
        <Select value={f.outcome_rating || "none"} onValueChange={(v) => setF({ ...f, outcome_rating: v === "none" ? "" : v })}>
          <SelectTrigger className="bg-white" data-testid="outcome-rating-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="none">Outcome not yet judged</SelectItem>{Object.entries(OUTCOMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {FIELDS.map(([k, l]) => <label key={k} className="block space-y-1"><span className="text-xs font-semibold text-stone-600">{l}</span><Textarea rows={2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`eval-${k}-input`} /></label>)}
        <Button size="sm" variant="outline" onClick={save} data-testid="evaluation-save">Save evaluation</Button>
      </div>
    </Box>
  );
};

const Rating = ({ value, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => onChange(n)} className={`h-8 w-8 rounded-md text-sm font-semibold transition-colors ${value >= n ? "bg-amber-900 text-amber-50" : "bg-[#F5F0E6] text-stone-600"}`} data-testid={`${testId}-${n}`}>{n}</button>)}
  </div>
);

export const RatingForm = ({ value, onChange, idx = "" }) => (
  <div className="grid gap-3 sm:grid-cols-3">
    <div><p className="text-xs text-stone-600 mb-1">How useful was it? (1–5)</p><Rating value={value.usefulness} onChange={(n) => onChange({ ...value, usefulness: n })} testId={`usefulness${idx}`} /></div>
    <div><p className="text-xs text-stone-600 mb-1">How easy was it to attend / access? (1–5)</p><Rating value={value.accessibility} onChange={(n) => onChange({ ...value, accessibility: n })} testId={`accessibility${idx}`} /></div>
    <div><p className="text-xs text-stone-600 mb-1">Need further help?</p>
      <Select value={value.needs_more_help === null || value.needs_more_help === undefined ? "skip" : String(value.needs_more_help)} onValueChange={(v) => onChange({ ...value, needs_more_help: v === "skip" ? null : v === "true" })}>
        <SelectTrigger className="bg-white" data-testid={`more-help${idx}`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="skip">Prefer not to say</SelectItem><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
      </Select>
    </div>
  </div>
);

const blank = () => ({ usefulness: 0, accessibility: 0, needs_more_help: null });

const PaperForms = ({ plan, onSaved }) => {
  const [forms, setForms] = useState([blank()]);
  const [day, setDay] = useState(new Date().toLocaleDateString("en-CA"));
  const ok = forms.every((x) => x.usefulness && x.accessibility) && day;
  const save = async () => {
    try { await api.post(`/plans/${plan.plan_id}/feedback-forms`, { forms, session_date: day }); toast.success(`${forms.length} forms recorded`); setForms([blank()]); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-3 rounded-lg bg-[#F5F0E6] p-4" data-testid="paper-forms">
      <p className="text-xs font-semibold text-stone-700">Enter paper feedback forms (no names)</p>
      <label className="flex items-center gap-2 text-xs text-stone-600">Session date <Input type="date" className="h-8 w-40 bg-white" value={day} onChange={(e) => setDay(e.target.value)} data-testid="paper-session-date" /></label>
      {forms.map((x, i) => (
        <div key={i} className="flex items-start gap-2 border-t border-amber-700/10 pt-2">
          <span className="mt-6 text-xs text-stone-500">#{i + 1}</span>
          <div className="flex-1"><RatingForm value={x} onChange={(v) => setForms(forms.map((y, j) => (j === i ? v : y)))} idx={`-${i}`} /></div>
          {forms.length > 1 && <Button variant="ghost" size="icon" onClick={() => setForms(forms.filter((_, j) => j !== i))} data-testid={`paper-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setForms([...forms, blank()])} data-testid="paper-add-form"><Plus className="h-4 w-4 mr-1" />Another form</Button>
        <Button size="sm" disabled={!ok} onClick={save} className="bg-[#285E3B] hover:bg-[#1E462C]" data-testid="paper-save">Save forms</Button>
      </div>
    </div>
  );
};

const FeedbackTrend = ({ planId, version }) => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/plans/${planId}/feedback-trend`).then((r) => setT(r.data)).catch(() => setT(null)); }, [planId, version]);
  if (!t || !t.sessions.length) return null;
  const data = t.sessions.map((s) => ({ ...s, label: s.date.length > 10 ? `${s.date.slice(5, 10)}…${s.date.slice(-5)}` : s.date.slice(5) }));
  return (
    <div className="mt-4" data-testid="feedback-trend">
      <p className="text-xs font-semibold text-stone-700">Ratings from session to session</p>
      {data.some((s) => !s.suppressed) && (
        <div className="mt-2 h-52"><ResponsiveContainer><LineChart data={data}><CartesianGrid stroke="#F5F0E6" /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
          <Line dataKey="avg_usefulness" name="Usefulness" stroke="#78350F" strokeWidth={2} connectNulls={false} />
          <Line dataKey="avg_accessibility" name="Accessibility" stroke="#285E3B" strokeWidth={2} connectNulls={false} /></LineChart></ResponsiveContainer></div>
      )}
      <table className="mt-2 w-full text-xs" data-testid="feedback-trend-table"><thead><tr className="text-left text-stone-500"><th className="py-1">Day</th><th>Session</th><th>Responses</th><th>Useful</th><th>Accessible</th><th>Need more help</th></tr></thead>
        <tbody>{t.sessions.map((s) => (
          <tr key={s.date} className="border-t border-amber-700/10"><td className="py-1">{s.date}</td><td>{s.session || "—"}</td><td className="font-mono-data">{s.responses}</td>
            {s.suppressed ? <td colSpan={3} className="text-stone-400">hidden (fewer than 5)</td> : <><td className="font-mono-data">{s.avg_usefulness}</td><td className="font-mono-data">{s.avg_accessibility}</td><td>{s.pct_needs_more_help !== null ? `${s.pct_needs_more_help}%` : "—"}</td></>}</tr>
        ))}</tbody></table>
      <p className="mt-1 text-[11px] text-stone-400">{t.note}</p>
    </div>
  );
};

export const FeedbackCard = ({ plan }) => {
  const [s, setS] = useState(null);
  const [v, setV] = useState(0);
  const load = () => api.get(`/plans/${plan.plan_id}/feedback-summary`).then((r) => { setS(r.data); setV((x) => x + 1); });
  useEffect(() => { load(); }, [plan.plan_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  return (
    <Box title="Participant feedback" testId="feedback-summary-card">
      <p className="text-xs text-stone-500">Voluntary and anonymous. Averages appear once at least 5 people respond.</p>
      <p className="mt-2 text-sm" data-testid="feedback-responses"><b>{s.responses}</b> responses</p>
      {s.suppressed && <p className="text-sm text-stone-500">Details hidden until there are 5 or more responses.</p>}
      {s.avg_usefulness !== undefined && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm" data-testid="feedback-averages">
          <p>Usefulness: <b>{s.avg_usefulness}</b>/5 ({s.pct_found_useful}% rated 4–5)</p>
          <p>Accessibility: <b>{s.avg_accessibility}</b>/5 ({s.pct_found_accessible}% rated 4–5)</p>
          <p className="col-span-2">Asked for further help: {s.needs_more_help_yes} of {s.needs_more_help_answered} who answered</p>
        </div>
      )}
      {s.comments?.length > 0 && <ul className="mt-3 space-y-1 text-xs text-stone-700">{s.comments.map((c, i) => <li key={i}>“{c}”</li>)}</ul>}
      <FeedbackTrend planId={plan.plan_id} version={v} />
      {plan.can.monitor && <PaperForms plan={plan} onSaved={load} />}
    </Box>
  );
};
